<template>
  <div class="message" v-if="orderData">
    <div class="row mt-1 mb-1 message">
      <div class="col-auto mt-2">
        <Avatar :img="integrationDetails.img" size="xs" class="timeline-integration-avatar" />
      </div>
      <div class="col ml--2">
        <div class="text-left promo comment-body">
          <div
            class="order-content"
            @click="!hideOrderDetails && toggleExpanded()"
            :class="{ expanded: isExpanded, 'no-expand': hideOrderDetails }"
          >
            <div v-show="orderNumber" class="order-number text-capitalize">{{ orderNumber }}</div>
            <div class="comment-text" role="alert">
              <span>
                Spent
                <strong>{{ orderTotal | currency }}</strong>
                <span v-if="orderData.items.length">
                  on
                  <span>{{ itemsSummary }}</span>
                </span>
                via {{ integrationDetails.name }}
              </span>
              <span class="badge badge-soft-primary ml-1 mb-1 mt-n1">Order</span>
              <div class="row">
                <div class="col-auto">
                  <p class="comment-time">
                    {{ created | moment('LT') }}
                    <span v-if="timezone">{{ timezone }}</span>
                    &#8226;
                    {{ location.friendlyName || location.city }}
                  </p>
                </div>
                <div v-if="!hideOrderDetails" class="col-auto ml-auto">
                  <div class="expand-toggle" :class="{ rotated: isExpanded }">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <polyline points="6,9 12,15 18,9"></polyline>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <transition name="expand" @enter="enter" @leave="leave">
            <div v-show="isExpanded && !hideOrderDetails" class="order-details">
              <div v-if="orderLoading || !detailedOrderData" class="order-loading">
                <div class="loading-spinner"></div>
                <span>Loading order details...</span>
              </div>
              <div v-else>
                <div class="order-section">
                  <h4 class="section-title">Order Details</h4>
                  <div v-if="detailedOrderData" class="order-meta-grid">
                    <div class="meta-item">
                      <div class="d-flex justify-content-between">
                        <div class="meta-label">Order ID:</div>
                        <div
                          class="fe fe-copy px-2 cursor-pointer fa-xs"
                          v-clipboard:copy="detailedOrderData?.externalOrder?.id"
                          v-clipboard:success="onCopyToClipboard"
                        ></div>
                      </div>
                      <span
                        class="meta-value"
                        :title="detailedOrderData?.externalOrder?.id"
                        v-if="detailedOrderData?.externalOrder?.id"
                      >
                        {{ detailedOrderData?.externalOrder?.id | truncate(16) }}
                      </span>
                    </div>
                    <div class="meta-item">
                      <span class="meta-label">Order Type:</span>
                      <span class="meta-value">{{
                        detailedOrderData?.fulfillmentMethod
                          ? detailedOrderData.fulfillmentMethod.toUpperCase()
                          : '-'
                      }}</span>
                    </div>
                    <div class="meta-item">
                      <span class="meta-label">Channel:</span>
                      <span class="meta-value">{{
                        !detailedOrderData?.channel ||
                        detailedOrderData.channel?.toLowerCase() === 'unknown'
                          ? '-'
                          : detailedOrderData.channel
                      }}</span>
                    </div>

                    <div class="meta-item">
                      <span class="meta-label">Placed:</span>
                      <span class="meta-value">{{ formattedPlacedDate }}</span>
                    </div>

                    <div class="meta-item">
                      <span class="meta-label">Table:</span>
                      <span class="meta-value">
                        {{ detailedOrderData?.table?.name || '-' }}
                      </span>
                    </div>
                    <div class="meta-item">
                      <div class="d-flex justify-content-between">
                        <div class="meta-label">Employee ID:</div>
                        <div
                          class="fe fe-copy px-2 cursor-pointer fa-xs"
                          v-clipboard:copy="detailedOrderData?.externalEmployee?.id"
                          v-clipboard:success="onCopyToClipboard"
                        ></div>
                      </div>
                      <span class="meta-value">
                        {{
                          detailedOrderData?.externalEmployee?.name ||
                          (detailedOrderData?.externalEmployee?.id
                            ? `${detailedOrderData.externalEmployee.id}`
                            : '-') | truncate(16)
                        }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="order-section">
                  <h4 class="section-title">Order Items</h4>
                  <div v-if="detailedOrderData?.items?.length" class="items-list">
                    <div
                      v-for="item in detailedOrderData.items"
                      :key="item?.externalId || item?.name || Math.random()"
                      class="order-item"
                    >
                      <div class="item-main">
                        <div class="item-info">
                          <span class="item-quantity">{{ item?.quantity || 1 }}×</span>
                          <span class="item-name">{{ item?.name || 'Unknown Item' }}</span>
                          <span class="item-price"
                            >${{
                              item?.totalCents ? (item.totalCents / 100).toFixed(2) : '-'
                            }}</span
                          >
                        </div>
                        <div v-if="item?.modifiers?.length" class="item-modifiers">
                          <span
                            v-for="modifier in item.modifiers"
                            :key="modifier?.externalId || modifier?.name || Math.random()"
                            class="modifier"
                          >
                            {{ modifier?.name || 'Unknown Modifier' }}
                            <span v-if="modifier?.totalCents > 0"
                              >+${{ (modifier.totalCents / 100).toFixed(2) }}</span
                            >
                          </span>
                        </div>
                        <div v-if="item?.note" class="item-note">
                          {{ item.note }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="no-items">No items available</div>
                </div>

                <div class="order-section">
                  <h4 class="section-title">Order Summary</h4>

                  <div v-if="detailedOrderData?.amounts" class="summary-lines">
                    <div v-if="detailedOrderData.amounts?.subtotalCents > 0" class="summary-line">
                      <span>Subtotal</span>
                      <span>${{ (detailedOrderData.amounts.subtotalCents / 100).toFixed(2) }}</span>
                    </div>
                    <div v-if="detailedOrderData.amounts?.taxTotalCents > 0" class="summary-line">
                      <span>Tax</span>
                      <span>${{ (detailedOrderData.amounts.taxTotalCents / 100).toFixed(2) }}</span>
                    </div>
                    <div v-if="detailedOrderData.amounts?.tipTotalCents > 0" class="summary-line">
                      <span>Tip</span>
                      <span>${{ (detailedOrderData.amounts.tipTotalCents / 100).toFixed(2) }}</span>
                    </div>
                    <div
                      v-if="detailedOrderData.amounts?.serviceChargeTotalCents > 0"
                      class="summary-line"
                    >
                      <span>Service Charge</span>
                      <span
                        >${{
                          (detailedOrderData.amounts.serviceChargeTotalCents / 100).toFixed(2)
                        }}</span
                      >
                    </div>
                    <div
                      v-if="detailedOrderData.amounts?.discountTotalCents > 0"
                      class="summary-line discount"
                    >
                      <span>Discount</span>
                      <span
                        >-${{
                          (detailedOrderData.amounts.discountTotalCents / 100).toFixed(2)
                        }}</span
                      >
                    </div>
                    <div class="summary-line total">
                      <span>Total</span>
                      <span
                        >${{
                          detailedOrderData.amounts?.grandTotalCents
                            ? (detailedOrderData.amounts.grandTotalCents / 100).toFixed(2)
                            : '-'
                        }}</span
                      >
                    </div>
                  </div>
                  <div v-else class="no-summary">Order summary not available</div>

                  <div v-if="detailedOrderData?.tenders?.length" class="payment-section">
                    <h5 class="payment-title">Payment Method</h5>
                    <div class="payment-methods">
                      <div
                        v-for="tender in detailedOrderData.tenders"
                        :key="tender?.externalId || tender?.name || Math.random()"
                        class="payment-method"
                      >
                        <div class="payment-icon">
                          <svg
                            v-if="tender?.type === 'CARD'"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                          >
                            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                            <line x1="1" y1="10" x2="23" y2="10"></line>
                          </svg>
                          <svg
                            v-else
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                          >
                            <line x1="12" y1="1" x2="12" y2="23"></line>
                            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                          </svg>
                        </div>
                        <div
                          class="payment-details"
                          :class="{ 'text-center': !tender?.card?.last4 }"
                        >
                          <span class="payment-name">{{ formatTenderName(tender) }}</span>
                          <span v-if="tender?.card?.last4" class="card-details">
                            {{ tender.card?.brand || 'Card' }} ••{{ tender.card.last4 }}
                          </span>
                        </div>
                        <div class="payment-amount">
                          ${{ tender?.amountCents ? (tender.amountCents / 100).toFixed(2) : '-' }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from '@/components/MainContent/Avatar'
import { getIntegrationDetails, availableIntegrations } from '@/lib/modules/integration'
import { formatTenderName } from '@/lib/format'
import { createNamespacedHelpers } from 'vuex'
const IntegrationModule = createNamespacedHelpers('integration')
const IntegrationProviderModule = createNamespacedHelpers('integrationProvider')
const WinbackModule = createNamespacedHelpers('winback')

export default {
  name: 'OrderEvent',
  props: [
    'created',
    'integration',
    'location',
    'order',
    'timezone',
    'eventId',
    'companyId',
    'hideOrderDetails'
  ],
  components: {
    Avatar
  },
  data: () => ({
    integrationProviderId: null,
    isExpanded: false,
    isLoadingThisOrder: false
  }),
  computed: {
    ...IntegrationProviderModule.mapGetters(['selectIntegrationProviderById']),
    ...WinbackModule.mapGetters(['selectDetailedOrderById']),
    integrationDetails() {
      let name = null
      if (
        this.integrationProviderId &&
        this.selectIntegrationProviderById(this.integrationProviderId)
      ) {
        let { displayName, logo } = this.selectIntegrationProviderById(this.integrationProviderId)
        return { name: displayName, img: logo }
      } else {
        name = this.integration?.provider
      }

      return getIntegrationDetails(name) || {}
    },
    orderData() {
      return this.order
    },
    detailedOrderData() {
      return this.order?._id ? this.selectDetailedOrderById(this.order._id) : null
    },
    orderLoading() {
      return this.isLoadingThisOrder
    },
    statusClass() {
      const status = this.orderData.status?.toLowerCase()
      return `status-${status}`
    },
    itemsSummary() {
      const items = this.orderData?.items || []
      if (!items.length) return null
      return items.reduce((itemString, item, index) => {
        if (itemString) {
          itemString += ', '
          if (index === items.length - 1) itemString += 'and '
        }
        if (item.quantity > 1) itemString += `(${item.quantity}) `
        return itemString + item.name
      }, '')
    },
    orderNumber() {
      const { posOrderNumber, orderProvider } = this.order || {}
      return posOrderNumber && posOrderNumber.length < 20
        ? `${
            orderProvider &&
            orderProvider?.toLowerCase() !== this.integrationDetails.name?.toLowerCase()
              ? `${orderProvider} `
              : ''
          }ORDER #${posOrderNumber}`
        : ''
    },
    items() {
      const items = this.order?.items || []
      if (!items.length) return null
      return items.reduce((itemString, item, index) => {
        if (itemString) {
          itemString += ', '
          if (index === items.length - 1) itemString += 'and '
        }
        if (item.quantity > 1) itemString += `(${item.quantity}) `
        const itemOptions = item?.options?.length ? this.joinOrderItemOptions(item?.options) : null
        return itemString + item.name + `${itemOptions ? itemOptions : ''}`
      }, '')
    },
    orderTotal() {
      if (!this.order) return 0
      const total =
        typeof this.order.total === 'object' &&
        this.order.total !== null &&
        '$numberDecimal' in this.order.total
          ? this.order.total.$numberDecimal
          : this.order.total

      return this.integration.provider === availableIntegrations.skipTheDishes().provider
        ? total / 100
        : total
    },
    formattedPlacedDate() {
      if (!this.detailedOrderData?._timestamps?.placedAt) {
        return '-'
      }
      return this.$options.filters.moment(
        this.detailedOrderData._timestamps.placedAt,
        'MMMM Do, YYYY [at] LT'
      )
    }
  },
  watch: {
    detailedOrderData(newData, oldData) {
      if (newData && !oldData && this.isExpanded) {
        this.$nextTick(() => {
          this.adjustHeight()
        })
      }
    }
  },
  methods: {
    ...IntegrationModule.mapActions(['getIntegration']),
    ...WinbackModule.mapActions(['fetchDetailedOrder']),
    async toggleExpanded() {
      this.isExpanded = !this.isExpanded

      if (
        this.isExpanded &&
        this.order?._id &&
        !this.selectDetailedOrderById(this.order._id) &&
        !this.isLoadingThisOrder
      ) {
        try {
          this.isLoadingThisOrder = true
          await this.fetchDetailedOrder(this.order._id)
        } catch (error) {
          console.error('Error fetching detailed order:', error)
        } finally {
          this.isLoadingThisOrder = false
        }
      }
    },
    onCopyToClipboard() {
      this.$notify({
        title: 'Copied to clipboard!'
      })
    },
    formatTenderName,
    enter(el) {
      el.style.height = 'auto'
      const height = getComputedStyle(el).height
      el.style.height = '0'
      el.offsetHeight
      el.style.transition = 'height 0.3s ease'
      el.style.height = height

      setTimeout(() => {
        el.style.height = 'auto'
      }, 300)
    },
    leave(el) {
      el.style.transition = 'height 0.3s ease'
      el.style.height = '0'
    },
    adjustHeight() {
      this.$nextTick(() => {
        const expandedEl = this.$el.querySelector('.order-details')
        if (expandedEl && this.isExpanded) {
          expandedEl.style.transition = 'none'
          expandedEl.style.height = 'auto'
          const newHeight = getComputedStyle(expandedEl).height
          expandedEl.style.transition = 'height 0.3s ease'
          expandedEl.style.height = newHeight
          setTimeout(() => {
            if (expandedEl.style.height !== '0px') {
              expandedEl.style.height = 'auto'
            }
          }, 300)
        }
      })
    },
    joinOrderItemOptions(options = []) {
      if (!options?.length) {
        return null
      }
      if (typeof options[0] === 'object') {
        options = options.map((opt) => opt.name)
      }
      if (!options?.length) {
        return null
      }
      // handle Fajita Pete Company's hungerRush integration only
      const fajitaPeteCompanyId = '61099e7a687c960009729cc0'
      if (this.integration?.provider !== 'hungerRush' && this.companyId !== fajitaPeteCompanyId) {
        return null
      }
      let optionsString = ''
      if (options.length > 1) {
        const lastItem = options[options.length - 1]
        const lastItemIndex = options.length - 1
        options = options.slice(0, lastItemIndex)
        optionsString = ` (${options?.join(', ')} & ${lastItem}) `
      } else {
        optionsString = ` (${options?.join(' ')}) `
      }
      return optionsString
    }
  },
  async mounted() {
    try {
      if (!Object.keys(this.integrationDetails).length) {
        const response = await this.getIntegration(this.integration._id)
        this.integrationProviderId = response.body.data.integration.integrationProvider
      }
    } catch (error) {
      console.log(`Error: No Integration details given as prop from analytic event ${this.eventId}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.order-number {
  font-weight: bold;
}

.order-content.no-expand {
  cursor: default;

  &:hover {
    background-color: transparent;
  }
}

.order-content {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 4px;
  padding: 2px;
  margin: -2px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
}

.expand-toggle {
  color: #6b7280;
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }

  &.rotated {
    transform: rotate(180deg);
  }
}

.order-details {
  margin-top: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background-color: #fafafa;
  overflow: hidden;
  display: table;
  width: 350px;
  box-sizing: border-box;
}

.order-meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 12px;
}

.order-section {
  padding: 16px;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.order-item {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
}

.item-main {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.item-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-quantity {
  background: #f3f4f6;
  color: #374151;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  min-width: 24px;
  text-align: center;
}

.item-name {
  font-weight: 500;
  color: #111827;
  flex: 1;
  font-size: 14px;
}

.item-price {
  font-weight: 600;
  color: #059669;
  font-size: 14px;
}

.item-modifiers {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-left: 32px;
}

.modifier {
  background: #ede9fe;
  color: #5b21b6;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
}

.item-note {
  margin-left: 32px;
  font-style: italic;
  color: #6b7280;
  font-size: 12px;
  background: #f9fafb;
  padding: 6px 8px;
  border-radius: 3px;
  border-left: 2px solid #d1d5db;
}

.summary-lines {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
}

.summary-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 14px;

  &:last-child {
    border-bottom: none;
  }

  &.discount {
    color: #dc2626;
  }

  &.total {
    background: #f9fafb;
    font-weight: 600;
    color: #111827;
  }
}

.payment-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.payment-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.payment-methods {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.payment-method {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
}

.payment-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #f3f4f6;
  border-radius: 4px;
  color: #6b7280;
}

.payment-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;

  &.text-center {
    align-items: center;
    justify-content: center;
  }
}

.payment-name {
  font-weight: 500;
  color: #111827;
  font-size: 14px;
}

.card-details {
  font-size: 12px;
  color: #6b7280;
}

.payment-amount {
  font-weight: 600;
  color: #059669;
  font-size: 14px;
  text-align: right;
}

.no-items,
.no-summary {
  text-align: center;
  color: #6b7280;
  font-style: italic;
  padding: 20px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 8px;
}

.meta-label {
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.meta-value {
  font-weight: 500;
  color: #111827;
  font-size: 13px;
}

// Expand animation
.expand-enter-active,
.expand-leave-active {
  transition: height 0.3s ease;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  height: 0;
}

.order-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 20px;
  color: #6b7280;
  font-size: 14px;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

// Responsive design
@media (max-width: 768px) {
  .order-section {
    padding: 12px;
  }

  .item-info {
    flex-wrap: wrap;
  }

  .item-modifiers,
  .item-note {
    margin-left: 0;
  }
}
</style>
